import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { useLocation } from 'react-router-dom';
import { getPostHogConfig, isPostHogEnabled } from './config';

interface PostHogContextValue {
  isInitialized: () => boolean;
}

const PostHogContext = createContext<PostHogContextValue>({ isInitialized: () => false });

interface PostHogProviderProps {
  children: ReactNode;
}

// Module-level flag to track initialization across renders
let posthogInitialized = false;

/**
 * PostHog Provider Component
 * Initializes PostHog and provides context to the app
 * Handles SPA page view tracking automatically
 */
export function PostHogProvider({ children }: PostHogProviderProps) {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);
  const hasInitialized = useRef(false);

  // Initialize PostHog on mount
  useEffect(() => {
    if (hasInitialized.current || posthogInitialized || !isPostHogEnabled()) return;

    const config = getPostHogConfig();

    try {
      posthog.init(config.apiKey, config.options);
      posthogInitialized = true;
      hasInitialized.current = true;

      if (import.meta.env.DEV) {
        console.log('PostHog initialized successfully');
      }
    } catch (error) {
      console.warn('Failed to initialize PostHog:', error);
    }
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (!isPostHogEnabled() || !posthogInitialized) return;

    // Only track if path actually changed
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;

    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        path: location.pathname,
        search: location.search,
      });
    } catch (error) {
      console.warn('Failed to track page view:', error);
    }
  }, [location.pathname, location.search]);

  const contextValue: PostHogContextValue = {
    isInitialized: () => posthogInitialized,
  };

  return (
    <PostHogContext.Provider value={contextValue}>
      {children}
    </PostHogContext.Provider>
  );
}

/**
 * Hook to access PostHog context
 */
export function usePostHogContext(): PostHogContextValue {
  return useContext(PostHogContext);
}
